import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from 'hooks/toasts/useToast';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { useChangeTrackingLens, get, set } from '@totalsoft/change-tracking-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { Autocomplete, Button, TextField } from '@totalsoft/rocket-ui';
import { COMMISSION_CALC_TYPES_DICTIONARY_QUERY } from 'features/common/queries';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import { SAVE_LEASING_COMMISSION } from '../list/mutations';
import { LEASING_COMMISSION_ASSIGNED_TO_LIST } from '../list/queries';
import { LEASING_COMMISSION_SAVED } from '../list/subscriptions';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { leasingCommissionModalVar } from '../variables';
import { Prompt } from 'react-router-dom';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { leasingCommissionValidator } from '../validations';
import { ADMIN_COMMISSIONS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';
import { leasingCommissionsStore } from '../list/components/LeasingCommissionListBrowser';

const EditComponent = ({ leasingCommission }) => {
  const { store } = useListBrowserUtils(leasingCommissionsStore);
  const addToast = useToast();
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_COMMISSIONS_WRITE);
  const { cvsChPercent, cvsChAmount } = useFieldDecimalPlacesConfig();

  const [saving, setSaving] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(leasingCommission);
  const [validation, validate] = useDirtyFieldValidation(leasingCommissionValidator);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const { data: commissionCalcTypesDictionaryData } = useQuery(COMMISSION_CALC_TYPES_DICTIONARY_QUERY);

  const { data: commissionAssignedToData } = useQuery(LEASING_COMMISSION_ASSIGNED_TO_LIST);

  useEffect(() => {
    const { ...rest } = leasingCommission;
    resetLens(rest);
  }, [leasingCommission, resetLens]);

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, dirtyInfo, lens]);

  const [updateLeasingCommission] = useMutation(SAVE_LEASING_COMMISSION, {
    onCompleted: _ => {},
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const input = get(lens);
    if (!validate(input)) return;
    setSaving(true);
    updateLeasingCommission({ variables: { input } });
  }, [lens, updateLeasingCommission, validate]);

  useSubscription(LEASING_COMMISSION_SAVED, {
    onData: _ => {
      addToast(t('LeasingCommission.Saved'), 'success');
      leasingCommissionModalVar({ open: false });
      setSaving(false);
      store.refetch();
    }
  });

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            disabled={!canWrite}
            fullWidth
            label={t('LeasingCommission.CommissionCode')}
            required
            value={get(lens.commissionCode) || ''}
            onChange={event => set(lens.commissionCode)(event)}
            error={!isValid(validation.commissionCode)}
            helperText={getErrors(validation.commissionCode)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            disabled={!canWrite}
            fullWidth
            label={t('LeasingCommission.CommissionName')}
            required
            value={get(lens.commissionName) || ''}
            onChange={event => set(lens.commissionName)(event)}
            error={!isValid(validation.commissionName)}
            helperText={getErrors(validation.commissionName)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            disabled={!canWrite}
            isNumeric
            fullWidth
            label={t('LeasingCommission.CommissionPercent')}
            decimalScale={cvsChPercent}
            value={get(lens.commissionPercent) || 0}
            onChange={set(lens.commissionPercent)}
            error={!isValid(validation.commissionPercent)}
            helperText={getErrors(validation.commissionPercent)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            disabled={!canWrite}
            isNumeric
            fullWidth
            label={t('LeasingCommission.CommissionValue')}
            decimalScale={cvsChAmount}
            value={get(lens.commissionValue) || 0}
            onChange={set(lens.commissionValue)}
            error={!isValid(validation.commissionValue)}
            helperText={getErrors(validation.commissionValue)}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            isSearchable
            isClearable
            simpleValue
            valueKey={'assignToRoleId'}
            labelKey={'assignToRole'}
            label={t('LeasingCommission.AssignedTo')}
            value={get(lens.assignToRoleId) || ''}
            onChange={set(lens.assignToRoleId)}
            options={
              commissionAssignedToData && commissionAssignedToData.leasingCommissionAssignedToList
                ? commissionAssignedToData.leasingCommissionAssignedToList
                : []
            }
            inputProps={{
              maxLength: 255
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            isSearchable
            simpleValue
            required
            valueKey={'id'}
            labelKey={'name'}
            label={t('LeasingCommission.CalculationType')}
            value={get(lens.calcTypeId) || ''}
            onChange={set(lens.calcTypeId)}
            options={
              commissionCalcTypesDictionaryData && commissionCalcTypesDictionaryData.commissionCalcTypesDictionary
                ? commissionCalcTypesDictionaryData.commissionCalcTypesDictionary
                : []
            }
            inputProps={{
              maxLength: 255
            }}
            error={!isValid(validation.calcTypeId)}
            helperText={getErrors(validation.calcTypeId)}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControlLabel
            disabled={!canWrite}
            label={t('LeasingCommission.ReadOnly')}
            control={
              <Checkbox
                checked={get(lens.isReadOnly) || false}
                onChange={event => set(lens.isReadOnly)(event.target.checked)}
                color='primary'
              />
            }
          />
        </Grid>
        <Grid item xs={4}>
          <FormControlLabel
            disabled={!canWrite}
            label={t('LeasingCommission.IsSubsidy')}
            control={
              <Checkbox
                checked={get(lens.isSubsidy) || false}
                onChange={event => set(lens.isSubsidy)(event.target.checked)}
                color='primary'
              />
            }
          />
        </Grid>
        <Grid item xs={4}>
          <FormControlLabel
            disabled={!canWrite}
            label={t('LeasingCommission.IsCommission')}
            control={
              <Checkbox
                checked={get(lens.isCommission) || false}
                onChange={event => set(lens.isCommission)(event.target.checked)}
                color='primary'
              />
            }
          />
        </Grid>
        <Grid container item xs={12} gap={1} justifyContent='flex-end'>
          <Button onClick={handleSave} disabled={saving || !canWrite}>
            {saving ? t('Saving') : t('Save')}
          </Button>
          <Button onClick={() => leasingCommissionModalVar({ open: false })}>{t('Close')}</Button>
        </Grid>
      </Grid>
    </>
  );
};

EditComponent.propTypes = {
  leasingCommission: PropTypes.object.isRequired
};

export default EditComponent;
